import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, RoleSwitcher, useAuth as useAuthContext } from './contexts/AuthContext';
import { OnboardingProvider, useOnboarding } from './contexts/OnboardingContext';

import Sidebar from './components/common/Sidebar';
import Header from './components/common/Header';
import { BusinessSuiteNavigation } from './components/business-suite-navigation';
import { useAuth } from './components/business-suite-navigation';
import FinancialDashboard from './components/dashboard/FinancialDashboard';
import OnboardingPage from './pages/OnboardingPage';
import UserGuidePage from './pages/UserGuidePage';
import FAQPage from './pages/FAQPage';
import AIChat from './pages/AIChat';
import FinancialAdvisor from './pages/FinancialAdvisor';
import DocumentScanner from './pages/DocumentScanner';
import AgentDashboard from './pages/AgentDashboard';
import EmployeeHub from './components/employees/EmployeeHub';
import EmployeeManagement from './components/employees/EmployeeManagement';
import PayrollManagement from './components/payroll/PayrollManagement';
import TimeTrackingDashboard from './components/time-tracking/TimeTrackingDashboard';
import TimeApprovalDashboard from './components/time-tracking/TimeApprovalDashboard';
import PerformanceDashboard from './components/performance/PerformanceDashboard';
import GoalManagement from './components/performance/GoalManagement';
import PerformanceReview from './components/performance/PerformanceReview';
import HRAnalyticsDashboard from './components/hr/HRAnalyticsDashboard';
import ChartOfAccounts from './components/accounting/ChartOfAccounts';
import TransactionEntry from './components/accounting/TransactionEntry';
import FinancialReports from './components/accounting/FinancialReports';
import { CustomerManagement } from './components/customers';
import { InvoiceManagement } from './components/invoices';
import { ExpenseManagement } from './components/expenses';
import { VATReturns } from './components/vat';
import { AdvancedReports } from './components/reports';
import { BankingIntegration } from './components/banking';
import { IntegrationManagement } from './components/integrations';
import CompaniesHouseIntegration from './components/integration/CompaniesHouseIntegration';
import FinancialAppMonitoring from './components/monitoring/FinancialAppMonitoring';

// Enterprise Features - Restored Endpoints
import SecurityDashboard from './pages/SecurityDashboard';
import ComplianceDashboard from './pages/ComplianceDashboard';
import AuditLog from './pages/AuditLog';
import BackupManagement from './pages/BackupManagement';
import AlertsManagement from './pages/AlertsManagement';
import UserManagement from './pages/UserManagement';
import DataRetention from './pages/DataRetention';
import HMRCIntegration from './pages/HMRCIntegration';
import HMRCPayment from './pages/HMRCPayment';
import CISManagement from './pages/CISManagement';
import Login from './pages/Login';
import PrivacyPolicy from './pages/PrivacyPolicy';
import LandingPage from './pages/LandingPage';
import SubscriptionPaywall, { EF_PRODUCTS } from './components/billing/SubscriptionPaywall';

// Create UK-themed Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2', // Professional blue
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e', // UK red accent
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    success: {
      main: '#2e7d32', // British racing green
    },
    warning: {
      main: '#ed6c02', // Amber
    },
    error: {
      main: '#d32f2f', // Red
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      color: '#1565c0',
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: '8px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '6px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
        },
      },
    },
  },
});

// Main App Content Component
const AppContent: React.FC = () => {
  const authContext = useAuthContext();
  const { isOnboardingComplete, isLoading: onboardingLoading, completeOnboarding } = useOnboarding();

  // Auto-complete onboarding for authenticated users so they go straight to dashboard
  React.useEffect(() => {
    if (authContext.firebaseUser && !isOnboardingComplete && !onboardingLoading) {
      completeOnboarding();
    }
  }, [authContext.firebaseUser, isOnboardingComplete, onboardingLoading, completeOnboarding]);

  // Don't render routes until auth and onboarding state are loaded to prevent flash
  if (onboardingLoading || authContext.loading) {
    return null;
  }

  // If not authenticated, show landing page
  if (!authContext.firebaseUser) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    );
  }

  // Convert auth context to BusinessSuiteNavigation format
  // Grant permissions for ALL registered app IDs so the nav dropdown shows every app
  const user = authContext.user ? {
    username: authContext.user.name || authContext.user.email || 'User',
    email: authContext.user.email || '',
    role: authContext.user.role || 'User',
    permissions: {
      'financial-uk': ['read', 'write', 'admin'],
      'forceai': ['read', 'write'],
      'crm': ['read', 'write'],
      'ascin': ['read', 'write'],
      'portal': ['read'],
      'hitl-monitoring': ['read', 'admin'],
    }
  } : undefined;

  const handleLogout = () => {
    authContext.logout();
  };

  return (
    <SubscriptionPaywall
      product={EF_PRODUCTS['ef-capflow']}
      getAuthToken={async () => {
        if (authContext.firebaseUser) {
          return await authContext.firebaseUser.getIdToken();
        }
        return null;
      }}
      userId={authContext.firebaseUser?.uid}
    >
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Executive Force AI Navigation */}
      <BusinessSuiteNavigation
        currentApp="financial-uk"
        user={user}
        onLogout={handleLogout}
      />

      {/* Main Content Area with Sidebar */}
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        <Sidebar />
        <Box component="main" sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          minHeight: 'calc(100vh - 36px)', // Account for navigation height
          ml: '280px', // Sidebar width
        }}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            {/* Main Dashboard */}
            <Route path="/dashboard" element={<AgentDashboard />} />
            <Route path="/financial-dashboard" element={<FinancialDashboard />} />

            {/* Help & Support Pages */}
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/user-guide" element={<UserGuidePage />} />
            <Route path="/faq" element={<FAQPage />} />

            {/* Employee Management Hub */}
            <Route path="/employees" element={<EmployeeHub />} />

            {/* Individual Employee Functions (accessible via hub or direct links) */}
            <Route path="/employee-directory" element={<EmployeeManagement />} />
            <Route path="/payroll" element={<PayrollManagement />} />
            <Route path="/time-tracking" element={<TimeTrackingDashboard />} />
            <Route path="/time-approval" element={<TimeApprovalDashboard />} />
            <Route path="/performance" element={<PerformanceDashboard />} />
            <Route path="/goals" element={<GoalManagement />} />
            <Route path="/reviews" element={<PerformanceReview />} />
            <Route path="/hr-analytics" element={<HRAnalyticsDashboard />} />

            {/* Finance & Accounting Routes */}
            <Route path="/finance" element={<ChartOfAccounts />} />
            <Route path="/chart-of-accounts" element={<ChartOfAccounts />} />
            <Route path="/transactions" element={<TransactionEntry />} />
            <Route path="/transaction-entry" element={<TransactionEntry />} />
            <Route path="/reports" element={<FinancialReports />} />
            <Route path="/financial-reports" element={<FinancialReports />} />

            {/* Customer Management */}
            <Route path="/customers" element={<CustomerManagement />} />

            <Route path="/invoices" element={<InvoiceManagement />} />

            <Route path="/expenses" element={<ExpenseManagement />} />

            <Route path="/vat" element={<VATReturns />} />

            <Route path="/banking" element={<BankingIntegration />} />

            <Route path="/advanced-reports" element={<AdvancedReports />} />

            <Route path="/integration" element={<IntegrationManagement />} />
            <Route path="/companies-house" element={<CompaniesHouseIntegration />} />

            {/* Monitoring */}
            <Route path="/monitoring" element={<FinancialAppMonitoring />} />

            {/* AI Features */}
            <Route path="/ai-chat" element={<AIChat />} />
            <Route path="/financial-advisor" element={<FinancialAdvisor />} />
            <Route path="/document-scanner" element={<DocumentScanner />} />
            <Route path="/ai-agents" element={<AgentDashboard />} />

            {/* Login - redirect to dashboard if already authenticated */}
            <Route path="/login" element={authContext.firebaseUser ? <Navigate to="/dashboard" replace /> : <Login />} />

            {/* Enterprise Features - Security & Compliance */}
            <Route path="/security" element={<SecurityDashboard />} />
            <Route path="/compliance" element={<ComplianceDashboard />} />
            <Route path="/audit" element={<AuditLog />} />
            <Route path="/backup" element={<BackupManagement />} />
            <Route path="/data-retention" element={<DataRetention />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/alerts" element={<AlertsManagement />} />
            <Route path="/users" element={<UserManagement />} />

            {/* HMRC Integration */}
            <Route path="/hmrc" element={<HMRCIntegration />} />
            <Route path="/hmrc/pay" element={<HMRCPayment />} />

            {/* CIS - Construction Industry Scheme */}
            <Route path="/cis" element={<CISManagement />} />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Box>
      </Box>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4caf50',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#f44336',
              secondary: '#fff',
            },
          },
        }}
      />
    </Box>
    </SubscriptionPaywall>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <OnboardingProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <RoleSwitcher />
          <Router>
            <AppContent />
          </Router>
        </ThemeProvider>
      </OnboardingProvider>
    </AuthProvider>
  );
};

export default App;
